import { useMemo, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Footprints } from 'lucide-react';
import { AdvancedFilterPanel, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { ErrorState } from '@/components/common/ErrorState';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { useAmFilterOptions } from '@/hooks/useAmFilterOptions';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { useKeepWindowScrollOnReturn } from '@/hooks/useKeepWindowScrollOnReturn';
import { useAuthStore } from '@/store/auth.store';
import { getUniqueBy } from '@/utils/uniqueBy';
import { moTravelListInfiniteQueryOptions } from '../api/moTravel.api';
import { getMoTravelColumns } from '../components/MoTravelColumns';
import { MoTravelListHeader } from '../components/MoTravelListHeader';
import { MoTravelStatsCards } from '../components/MoTravelStatsCards';
import { MO_TRAVEL_LIST_PAGE_SIZE, MO_TRAVEL_SORT_COLUMNS } from '../constants/moTravel.constants';
import { useMoTravelFilterFields } from '../hooks/useMoTravelFilterFields';
import { useLoadPagesUntilMoFound } from '../hooks/useLoadPagesUntilMoFound';
import { useMoTravelListSearch } from '../hooks/useMoTravelListSearch';
import { useLastViewedMoStore } from '../store/lastViewedMo.store';
import type { MoTravelListItem } from '../types/moTravel.types';

function getAmGroupKey(mo: MoTravelListItem) {
  return mo.amUserId;
}

export function MoTravelListPage() {
  const navigate = useNavigate();
  const searchState = useMoTravelListSearch();
  const { search, period, statsParams, listParams } = searchState;
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const { fields, hasOptionsError } = useMoTravelFilterFields({
    isFilterPanelOpen: isFilterOpen,
    companyId: searchState.draftFilters.companyId,
  });

  const { rows, loadedPageCount, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(moTravelListInfiniteQueryOptions(listParams));
  const mos = useMemo(() => getUniqueBy(rows, (mo) => mo.userId), [rows]);

  const { amOptions } = useAmFilterOptions(true);
  const currentUserId = useAuthStore((state) => state.user?.id);
  const hasOtherAmRows = mos.some((mo) => mo.amUserId !== currentUserId);
  const isAmColumnVisible = amOptions ? amOptions.length > 0 : hasOtherAmRows;
  const isSorted = searchState.tableSort !== undefined;
  const columns = useMemo(
    () => getMoTravelColumns({ searchText: search.fullName, period, isAmColumnVisible }),
    [search.fullName, period, isAmColumnVisible],
  );
  const lastViewedMoId = useLastViewedMoStore((state) => state.lastViewedMoId);

  useKeepWindowScrollOnReturn(lastViewedMoId !== null);
  useLoadPagesUntilMoFound({
    moId: lastViewedMoId,
    mos,
    loadedPageCount,
    listParams,
    canLoadMore:
      loadMoreProps.hasMore && !loadMoreProps.isLoadingMore && !loadMoreProps.loadMoreError,
    onLoadMore: loadMoreProps.onLoadMore,
  });

  function handleRowClick(mo: MoTravelListItem) {
    void navigate({
      to: '/geo-location/mo-travel-history/$userId',
      params: { userId: mo.userId },
      search: period,
    });
  }

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <MoTravelListHeader
        searchState={searchState}
        isFilterOpen={isFilterOpen}
        onFilterToggle={() => setIsFilterOpen((wasOpen) => !wasOpen)}
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <div className="flex flex-col gap-2">
          <AdvancedFilterPanel
            fields={fields}
            values={searchState.draftFilters}
            onChange={searchState.handleFilterChange}
            onApply={searchState.applyFilterValues}
            hasPendingChanges={searchState.hasPendingChanges}
          />
          {hasOptionsError && (
            <p className="text-xs text-destructive">
              Some filter options could not be loaded. Refresh the page to try again.
            </p>
          )}
        </div>
      </FilterPanelCollapse>

      <MoTravelStatsCards params={statsParams} />

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load MO travel history"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <DataTable
          data={mos}
          columns={columns}
          isLoading={isLoading}
          className="lg:flex-1"
          emptyIcon={Footprints}
          emptyMessage="No MO activity in this period"
          skeletonRowCount={MO_TRAVEL_LIST_PAGE_SIZE}
          {...loadMoreProps}
          isRowHighlighted={(mo) => mo.userId === lastViewedMoId}
          onRowClick={handleRowClick}
          groupFirstColumnBy={isAmColumnVisible && !isSorted ? getAmGroupKey : undefined}
          sort={searchState.tableSort}
          onSortChange={searchState.handleSortChange}
          sortableColumnIds={MO_TRAVEL_SORT_COLUMNS}
        />
      )}
    </div>
  );
}
